import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Container,
  Chip,
  Grid,
  LinearProgress,
  Paper,
  Typography,
  Tabs,
  Tab,
} from "@mui/material";
import bkstApi from "../../api";
import StoreClosureV2 from "../components/StoreClosureV2";

function StoreSection({ title, description, children }) {
  return (
    <Box mb={3}>
      <Paper elevation={0} sx={{ mt: description ? 2 : 1 }}>
        <Box p={2}>
          <Typography variant="h4" component="h2" sx={{ fontWeight: 600, mb: 0 }}>
            {title}
          </Typography>
          {description ? (
            <Typography variant="body1" color="textSecondary" sx={{ mt: 1, mb: 0, maxWidth: 720 }}>
              {description}
            </Typography>
          ) : null}
          {children}
        </Box>
      </Paper>
    </Box>
  );
}

function TimeslotsSection({ storeLocation, mode }) {
  const timeslots = storeLocation.fulfillment[mode].timeslots;

  return (
    <Box sx={{ borderTop: "1px solid #ECECEC", pt: 2, mt: 2 }}>
      {["mon", "tue", "wed", "thu", "fri", "sat", "sun"].map((d) => (
        <Box my={1} key={d}>
          <Typography
            variant="h6"
            display="block"
            sx={{ fontWeight: "600", textTransform: "capitalize" }}
          >
            {d}
          </Typography>
          {timeslots[d]?.length === 0 && (
            <Typography variant="body1" display="block" color="error">
              Closed
            </Typography>
          )}
          {timeslots[d]?.map((t) => (
            <Chip key={`${d}-${t}`} label={t} sx={{ mr: 0.5, mb: 0.5 }} />
          ))}
        </Box>
      ))}
    </Box>
  );
}

export default function MyStoreLocations(props) {
  const [storeLocations, setStoreLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(0);

  const refetchStoreLocations = useCallback(
    () =>
      bkstApi(`/my/store-location`).then((res) => {
        setStoreLocations(res.data.storeLocations);
      }),
    []
  );

  useEffect(() => {
    setLoading(true);
    refetchStoreLocations().finally(() => setLoading(false));
  }, [refetchStoreLocations]);

  useEffect(() => {
    setTab((t) =>
      storeLocations.length ? Math.min(t, Math.max(0, storeLocations.length - 1)) : 0
    );
  }, [storeLocations]);

  if (loading) {
    return <LinearProgress />;
  }

  const selected = storeLocations[tab];
  // Checkout reads delivery hours and rules from the default location, else the first one.
  const deliveryLocationId = (storeLocations.find((o) => o.default) || storeLocations[0])?.id;
  const showDelivery = Boolean(selected) && selected.id === deliveryLocationId;
  const lat = +selected?.coordinates?.lat;
  const lng = +selected?.coordinates?.lng;
  const selectedCoords = Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null;

  return (
    <Container maxWidth="md">
      <Box>
        <Box my={6}>
          <Typography variant="h1" gutterBottom>
            Stores
          </Typography>
          <Typography variant="body1" color="textSecondary">
            Configure store pickup & delivery options shown to customers during online checkout on
            your website.
          </Typography>
        </Box>

        {storeLocations.length === 0 ? (
          <Typography variant="body2" color="textSecondary">
            No store locations found.
          </Typography>
        ) : (
          <>
            <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
              <Tabs
                value={tab}
                onChange={(_, v) => setTab(v)}
                variant="scrollable"
                scrollButtons="auto"
                aria-label="Store locations"
              >
                {storeLocations.map((o) => (
                  <Tab key={o.id} label={o.address.street} />
                ))}
              </Tabs>
            </Box>

            {selected && (
              <Box mb={3}>
                <Paper elevation={0} sx={{ mt: 1, p: 2 }}>
                  <Grid container spacing={2} alignItems="flex-start">
                    <Grid item xs={12} md={selectedCoords ? 6 : 12}>
                      <Typography variant="h4">{selected.address.street}</Typography>
                      <Typography variant="h4" sx={{ mt: 1, mb: 1 }}>
                        {selected.address.city}, {selected.address.state} {selected.address.zip}
                      </Typography>
                    </Grid>
                    {/*selectedCoords && (
                      <Grid item xs={12} md={6}>
                        <Box
                          sx={{
                            height: { xs: 220, sm: 240 },
                            width: "100%",
                            borderRadius: 1,
                            overflow: "hidden",
                            bgcolor: "grey.200",
                          }}
                        >
                          <Map
                            key={selected.id}
                            defaultCenter={selectedCoords}
                            defaultZoom={15}
                            gestureHandling="cooperative"
                            mapTypeControl={false}
                            style={{ width: "100%", height: "100%" }}
                          >
                            <Marker position={selectedCoords} />
                          </Map>
                        </Box>
                      </Grid>
                    )*/}
                  </Grid>
                </Paper>
              </Box>
            )}

            {selected && (
              <Box>
                <StoreSection
                  title="Pickup Timeslots"
                  description="Customers will only be able to choose from the following pickup hours during online checkout. For any changes, please contact Bakestreet using the Live Chat option."
                >
                  <TimeslotsSection storeLocation={selected} mode="pickup" />
                </StoreSection>

                {showDelivery && (
                  <StoreSection
                    title="Delivery Timeslots"
                    description="Customers will only be able to choose from the following delivery hours during online checkout. For any changes, please contact Bakestreet using the Live Chat option."
                  >
                    <TimeslotsSection storeLocation={selected} mode="delivery" />
                  </StoreSection>
                )}

                <StoreSection
                  title="Blackout & Special Hours"
                  description={
                    showDelivery
                      ? "Override pickup and delivery independently for specific dates during online checkout. Disable one channel, both, or limit timeslots to special hours only."
                      : "Override pickup for specific dates during online checkout. Disable pickup entirely or limit timeslots to special hours only."
                  }
                >
                  <StoreClosureV2
                    storeLocation={selected}
                    showDelivery={showDelivery}
                    onStoreLocationsUpdated={refetchStoreLocations}
                  />
                </StoreSection>
              </Box>
            )}
          </>
        )}
      </Box>
    </Container>
  );
}
